import { z } from 'zod'
import * as Sentry from '@sentry/react'

export const baseUrl = import.meta.env.VITE_API_BASE_URL

/**
 * Custom error class for HTTP errors with detailed request and response information
 */
export class HttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly statusText: string,
    public readonly url: string,
    public readonly method: string,
    public readonly responseBody?: string,
    public readonly requestBody?: string,
    public readonly requestHeaders?: Record<string, string>,
  ) {
    super(message)
    this.name = 'HttpError'
  }

  override toString(): string {
    const parts = [
      `${this.name}: ${this.message}`,
      `[${this.method} ${this.url}]`,
      `Status: ${this.status} ${this.statusText}`,
    ]

    if (this.requestHeaders) {
      parts.push(`\nRequest Headers: ${JSON.stringify(this.requestHeaders, null, 2)}`)
    }

    if (this.requestBody) {
      parts.push(`\nRequest Body: ${this.requestBody}`)
    }

    if (this.responseBody) {
      parts.push(`\nResponse Body: ${this.responseBody}`)
    }

    return parts.join('\n')
  }
}

/**
 * Custom error class for schema validation errors with detailed information
 */
export class ValidationError extends Error {
  constructor(
    message: string,
    public readonly url: string,
    public readonly method: string,
    public readonly validationError: z.ZodError,
    public readonly responseData: unknown,
  ) {
    super(message)
    this.name = 'ValidationError'
  }

  override toString(): string {
    return `${this.name}: ${this.message} [${this.method} ${this.url}]\nValidation errors: ${JSON.stringify(this.validationError.issues, null, 2)}\nReceived data: ${JSON.stringify(this.responseData, null, 2)}`
  }
}

/**
 * Core API fetch with comprehensive error handling
 * Handles HTTP errors, network errors, JSON parsing, and Zod validation
 */
async function apiFetch<T>(url: string, schema: z.ZodType<T>, options: RequestInit = {}): Promise<T> {
  const method = options.method || 'GET'

  // Capture request information for error reporting
  const requestBody = typeof options.body === 'string' ? options.body : undefined
  const requestHeaders = options.headers
    ? Object.fromEntries(
        Object.entries(options.headers).map(([key, value]) => [key, Array.isArray(value) ? value.join(', ') : value]),
      )
    : undefined

  // 1. Perform fetch and handle network errors
  let response: Response
  try {
    response = await fetch(url, options)
  } catch (error) {
    // AbortError can be a DOMException or Error with name 'AbortError'
    // Re-throw it as-is so TanStack Query can handle cancellations properly
    if (error instanceof Error && error.name === 'AbortError') {
      throw error
    }

    // For other network errors, log to Sentry and wrap with context
    const networkError = new Error(
      `Network error while fetching ${method} ${url}: ${error instanceof Error ? error.message : 'Unknown error'}`,
    )

    Sentry.captureException(networkError, {
      tags: {
        errorType: 'network-error',
        httpMethod: method,
      },
      extra: {
        url,
        requestBody,
        requestHeaders,
        originalError: error instanceof Error ? error.message : String(error),
      },
    })

    throw networkError
  }

  // 2. Handle HTTP errors (non-2xx status codes)
  if (!response.ok) {
    const contentType = response.headers.get('content-type')
    let responseBody: string | undefined

    try {
      if (contentType?.includes('application/json')) {
        const errorData: unknown = await response.json()
        responseBody = JSON.stringify(errorData, null, 2)
      } else {
        responseBody = await response.text()
      }
    } catch {
      // If reading error body fails, continue without it
      responseBody = undefined
    }

    const httpError = new HttpError(
      `HTTP ${response.status}: ${response.statusText}`,
      response.status,
      response.statusText,
      url,
      method,
      responseBody,
      requestBody,
      requestHeaders,
    )

    // Log errors to Sentry, except 400 — those are validation errors caused by
    // user input, which the calling UI handles itself. They stay visible in the
    // backend API log.
    if (response.status !== 400) {
      Sentry.captureException(httpError, {
        tags: {
          errorType: 'http-error',
          httpStatus: response.status,
          httpMethod: method,
        },
        extra: {
          url,
          requestBody,
          requestHeaders,
          responseBody,
          responseStatus: response.status,
          responseStatusText: response.statusText,
        },
      })
    }

    throw httpError
  }

  // 3. Parse JSON response
  let data: unknown
  try {
    data = await response.json()
  } catch (error) {
    const parseError = new Error(
      `Failed to parse JSON response from ${method} ${url}: ${error instanceof Error ? error.message : 'Unknown error'}`,
    )

    Sentry.captureException(parseError, {
      tags: {
        errorType: 'json-parse-error',
        httpMethod: method,
      },
      extra: {
        url,
        requestBody,
        requestHeaders,
      },
    })

    throw parseError
  }

  // 4. Validate response with Zod schema
  try {
    return schema.parse(data)
  } catch (error) {
    if (error instanceof z.ZodError) {
      const validationError = new ValidationError(
        `Response validation failed for ${method} ${url}`,
        url,
        method,
        error,
        data,
      )

      // Log detailed validation error for debugging (works in both dev and production)
      console.error('🔴 Zod Validation Error:', {
        url,
        method,
        validationIssues: error.issues.map((issue) => ({
          path: issue.path.join('.'),
          message: issue.message,
          code: issue.code,
          expected: 'expected' in issue ? issue.expected : undefined,
          received: 'received' in issue ? issue.received : undefined,
        })),
        receivedData: data,
      })

      Sentry.captureException(validationError, {
        tags: {
          errorType: 'validation-error',
          httpMethod: method,
        },
        extra: {
          url,
          requestBody,
          requestHeaders,
          validationIssues: error.issues,
          responseData: data,
        },
      })

      throw validationError
    }
    throw error
  }
}

/**
 * GET request with JSON response validation
 */
export function getJson<T>(url: string, schema: z.ZodType<T>, options: RequestInit = {}): Promise<T> {
  return apiFetch(url, schema, options)
}

/**
 * POST JSON data and validate response with Zod schema
 * Automatically sets Content-Type header and stringifies the body
 */
export function postJson<TRequest, TResponse>(
  url: string,
  body: TRequest,
  schema: z.ZodType<TResponse>,
  options: RequestInit = {},
): Promise<TResponse> {
  return apiFetch(url, schema, {
    ...options,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    body: JSON.stringify(body),
  })
}
